import type { CategoryDto, ImportResultDto } from '@deli/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import {
  uploadExtra,
  uploadHero,
  useCategories,
  useCreateCategory,
  useCreateProduct,
  useDeleteCategory,
  useImportCatalogue,
  useUpdateCategory,
} from '../api/catalogue.js';
import { ErrorText } from '../components/ErrorText.js';
import { Modal } from '../components/Modal.js';
import { parseMoneyInput } from '../format.js';
import { t } from '../i18n/uk.js';
import { FilePreviews } from './ProductImages.js';

const tc = t.catalogue;

/**
 * Admin-only catalogue tools (`catalogue.manage` / `catalogue.import`): add a product by hand,
 * manage categories, import a CSV. Each sits in a collapsed section so browsing stays first.
 */

/** The "new product" popup: details, opening stock, hero and extra images. */
function NewProductDialog({ onClose }: { onClose: () => void }) {
  const create = useCreateProduct();
  const client = useQueryClient();
  const categories = useCategories();
  const [form, setForm] = useState({
    sku: '',
    name: '',
    categoryPath: '',
    basePrice: '',
    costPrice: '',
    onHand: '0',
  });
  const [hero, setHero] = useState<File[]>([]);
  const [extras, setExtras] = useState<File[]>([]);
  const [invalid, setInvalid] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [imagesFailed, setImagesFailed] = useState(false);
  const set = (key: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm({ ...form, [key]: e.target.value });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const basePrice = parseMoneyInput(form.basePrice);
    const costPrice = form.costPrice.trim() ? parseMoneyInput(form.costPrice) : null;
    if (basePrice === null || (form.costPrice.trim() !== '' && costPrice === null)) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    create.mutate(
      {
        sku: form.sku,
        name: form.name,
        categoryPath: form.categoryPath.trim() || null,
        basePrice,
        costPrice,
        onHand: Number(form.onHand || 0),
      },
      {
        onSuccess: async (product) => {
          // The product exists now; images follow (hero first, extras in the chosen order).
          setUploading(true);
          try {
            if (hero[0]) await uploadHero(product.id, hero[0]);
            for (const file of extras) await uploadExtra(product.id, file);
            onClose();
          } catch {
            setImagesFailed(true);
          } finally {
            setUploading(false);
            void client.invalidateQueries({ queryKey: ['catalogue'] });
          }
        },
      },
    );
  };

  return (
    <Modal title={tc.newProduct} onClose={onClose}>
      <form className="form-grid" onSubmit={submit}>
        <label>
          {tc.sku}
          <input aria-label={tc.sku} required autoFocus value={form.sku} onChange={set('sku')} />
        </label>
        <label>
          {tc.name}
          <input aria-label={tc.name} required value={form.name} onChange={set('name')} />
        </label>
        <label>
          {tc.category}
          <input
            aria-label={tc.category}
            list="category-paths"
            placeholder={tc.noCategory}
            value={form.categoryPath}
            onChange={set('categoryPath')}
          />
          <datalist id="category-paths">
            {(categories.data ?? []).map((c) => (
              <option key={c.id} value={c.path} />
            ))}
          </datalist>
        </label>
        <label>
          {tc.basePrice}, ₴
          <input
            aria-label={tc.basePrice}
            inputMode="decimal"
            required
            value={form.basePrice}
            onChange={set('basePrice')}
          />
        </label>
        <label>
          {tc.costPrice}, ₴
          <input
            aria-label={tc.costPrice}
            inputMode="decimal"
            value={form.costPrice}
            onChange={set('costPrice')}
          />
        </label>
        <label>
          {tc.openingStock}
          <input
            aria-label={tc.openingStock}
            type="number"
            min={0}
            step={1}
            value={form.onHand}
            onChange={set('onHand')}
          />
        </label>
        <p className="muted hint">{tc.categoryHint}</p>
        <label>
          {tc.heroImage}
          <input
            aria-label={tc.heroImage}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={(e) => setHero([...(e.target.files ?? [])].slice(0, 1))}
          />
          <FilePreviews files={hero} label={tc.heroImage} />
        </label>
        <label>
          {tc.extraImages}
          <input
            aria-label={tc.extraImages}
            type="file"
            multiple
            accept="image/jpeg,image/png,image/webp"
            onChange={(e) => setExtras([...(e.target.files ?? [])])}
          />
          <FilePreviews files={extras} label={tc.extraImages} />
        </label>
        <p className="muted hint">{tc.imageHint}</p>
        <div className="actions">
          <button type="submit" className="primary" disabled={create.isPending || uploading}>
            {tc.createProduct}
          </button>
          <button type="button" onClick={onClose}>
            {tc.cancel}
          </button>
          {uploading && <span role="status">{tc.uploading}</span>}
        </div>
        {invalid && <p className="error-text">{t.common.invalidAmount}</p>}
        {imagesFailed && <p className="error-text">{tc.imagesFailed}</p>}
        <ErrorText error={create.error} />
      </form>
    </Modal>
  );
}

/** "＋ Новий товар" button that opens the popup. */
export function NewProductButton() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className="primary" onClick={() => setOpen(true)}>
        {tc.addProduct}
      </button>
      {open && <NewProductDialog onClose={() => setOpen(false)} />}
    </>
  );
}

function ParentSelect({
  label,
  value,
  onChange,
  categories,
  exclude,
}: {
  label: string;
  value: string;
  onChange: (id: string) => void;
  categories: CategoryDto[];
  /** The category being edited and everything below it can't become its parent. */
  exclude?: CategoryDto;
}) {
  const below = (c: CategoryDto) =>
    exclude !== undefined && (c.id === exclude.id || c.path.startsWith(`${exclude.path} / `));
  return (
    <select aria-label={label} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">{tc.topLevel}</option>
      {categories
        .filter((c) => !below(c))
        .map((c) => (
          <option key={c.id} value={c.id}>
            {c.path}
          </option>
        ))}
    </select>
  );
}

/** Name input + submit, used to add a root category or a subcategory. */
function AddCategoryForm({
  parentId,
  label,
  onDone,
}: {
  parentId: string | null;
  label: string;
  onDone?: () => void;
}) {
  const create = useCreateCategory();
  const [name, setName] = useState('');
  return (
    <form
      className="inline"
      onSubmit={(e) => {
        e.preventDefault();
        create.mutate(
          { name, parentId },
          {
            onSuccess: () => {
              setName('');
              onDone?.();
            },
          },
        );
      }}
    >
      <input
        aria-label={label}
        placeholder={tc.rename}
        required
        autoFocus={parentId !== null}
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <button type="submit" disabled={create.isPending}>
        {t.common.add}
      </button>
      {onDone && (
        <button type="button" onClick={onDone}>
          {tc.cancel}
        </button>
      )}
      <ErrorText error={create.error} />
    </form>
  );
}

type Mode = 'view' | 'edit' | 'add';

function CategoryNode({
  category,
  all,
  childrenOf,
  level,
  expanded,
  toggle,
}: {
  category: CategoryDto;
  all: CategoryDto[];
  childrenOf: Map<string | null, CategoryDto[]>;
  level: number;
  expanded: Set<string>;
  toggle: (id: string) => void;
}) {
  const update = useUpdateCategory();
  const remove = useDeleteCategory();
  const [mode, setMode] = useState<Mode>('view');
  const [name, setName] = useState(category.name);
  const [parentId, setParentId] = useState(category.parentId ?? '');
  const children = childrenOf.get(category.id) ?? [];
  const isOpen = expanded.has(category.id);

  return (
    <li
      role="treeitem"
      aria-level={level}
      aria-expanded={children.length > 0 ? isOpen : undefined}
      aria-label={category.name}
    >
      <div className="tree-row">
        {children.length > 0 ? (
          <button
            type="button"
            className="icon toggle"
            aria-label={`${isOpen ? tc.collapse : tc.expand}: ${category.name}`}
            onClick={() => toggle(category.id)}
          >
            {isOpen ? '▾' : '▸'}
          </button>
        ) : (
          <span className="toggle" />
        )}
        {mode === 'edit' ? (
          <form
            className="inline"
            onSubmit={(e) => {
              e.preventDefault();
              update.mutate(
                { id: category.id, name, parentId: parentId || null },
                { onSuccess: () => setMode('view') },
              );
            }}
          >
            <input
              aria-label={`${tc.rename}: ${category.path}`}
              autoFocus
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <ParentSelect
              label={`${tc.parent}: ${category.path}`}
              value={parentId}
              onChange={setParentId}
              categories={all}
              exclude={category}
            />
            <button type="submit" className="primary" disabled={update.isPending}>
              {t.common.save}
            </button>
            <button type="button" onClick={() => setMode('view')}>
              {tc.cancel}
            </button>
          </form>
        ) : (
          <>
            <span className="tree-name">{category.name}</span>
            <span className="badge" title={tc.products}>
              {category.productCount}
            </span>
            <span className="tree-actions">
              <button type="button" onClick={() => setMode('add')}>
                + {tc.addSubcategory}
              </button>
              <button
                type="button"
                onClick={() => {
                  setName(category.name);
                  setParentId(category.parentId ?? '');
                  setMode('edit');
                }}
              >
                {t.common.edit}
              </button>
              <button
                type="button"
                title={tc.deleteHint}
                disabled={category.productCount > 0 || children.length > 0 || remove.isPending}
                onClick={() => remove.mutate(category.id)}
              >
                {t.common.remove}
              </button>
            </span>
          </>
        )}
      </div>
      <ErrorText error={update.error ?? remove.error} />
      {mode === 'add' && (
        <div className="tree-add">
          <AddCategoryForm
            parentId={category.id}
            label={`${tc.addSubcategory}: ${category.path}`}
            onDone={() => {
              setMode('view');
              if (!isOpen) toggle(category.id);
            }}
          />
        </div>
      )}
      {children.length > 0 && isOpen && (
        <ul role="group">
          {children.map((child) => (
            <CategoryNode
              key={child.id}
              category={child}
              all={all}
              childrenOf={childrenOf}
              level={level + 1}
              expanded={expanded}
              toggle={toggle}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

/** Categories as an expandable tree: add, rename, move and delete (empty only) in place. */
export function CategoryTree() {
  const categories = useCategories();
  const all = categories.data ?? [];
  const childrenOf = new Map<string | null, CategoryDto[]>();
  for (const c of [...all].sort((a, b) => a.name.localeCompare(b.name, 'uk'))) {
    childrenOf.set(c.parentId, [...(childrenOf.get(c.parentId) ?? []), c]);
  }
  // Everything starts expanded; the user folds what they don't need.
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const expanded = new Set(all.map((c) => c.id).filter((id) => !collapsed.has(id)));
  const toggle = (id: string) =>
    setCollapsed((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <details className="card">
      <summary>{tc.categoriesTitle}</summary>
      <AddCategoryForm parentId={null} label={tc.addCategory} />
      <p className="muted hint">{tc.deleteHint}</p>
      <ul role="tree" aria-label={tc.categoriesTitle} className="tree">
        {(childrenOf.get(null) ?? []).map((root) => (
          <CategoryNode
            key={root.id}
            category={root}
            all={all}
            childrenOf={childrenOf}
            level={1}
            expanded={expanded}
            toggle={toggle}
          />
        ))}
      </ul>
    </details>
  );
}

function ImportSummary({ result }: { result: ImportResultDto }) {
  if (result.errors.length > 0) {
    return (
      <div role="alert" className="error-text">
        <p>{tc.errors}</p>
        <ul>
          {result.errors.map((e) => (
            <li key={`${e.line}-${e.message}`}>
              {tc.line} {e.line}: {e.message}
            </li>
          ))}
        </ul>
      </div>
    );
  }
  return (
    <div role="status">
      <p className={result.applied ? 'ok-text' : 'muted'}>
        {result.applied ? tc.applied : tc.preview}: {tc.created} {result.created} · {tc.updated}{' '}
        {result.updated} · {tc.unchanged} {result.unchanged}
      </p>
      {result.newCategories.length > 0 && (
        <p className="muted">
          {tc.newCategories}: {result.newCategories.join(', ')}
        </p>
      )}
    </div>
  );
}

/** CSV import (admin): check first (dry run), then apply. A file with errors writes nothing. */
export function ImportPanel() {
  const importer = useImportCatalogue();
  const [csv, setCsv] = useState('');
  const run = (dryRun: boolean) => importer.mutate({ csv, dryRun });
  return (
    <details className="card">
      <summary>{tc.importTitle}</summary>
      <p className="muted hint">{tc.importHint}</p>
      <div className="actions">
        <input
          aria-label={tc.file}
          type="file"
          accept=".csv,text/csv"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void file.text().then(setCsv);
          }}
        />
      </div>
      <textarea
        aria-label="CSV"
        rows={5}
        value={csv}
        onChange={(e) => setCsv(e.target.value)}
        placeholder="sku;name;category;base_price;cost_price;on_hand"
      />
      <div className="actions">
        <button
          type="button"
          disabled={!csv.trim() || importer.isPending}
          onClick={() => run(true)}
        >
          {tc.check}
        </button>
        <button
          type="button"
          className="primary"
          disabled={!csv.trim() || importer.isPending}
          onClick={() => run(false)}
        >
          {tc.apply}
        </button>
      </div>
      {importer.data && <ImportSummary result={importer.data} />}
      <ErrorText error={importer.error} />
    </details>
  );
}
